import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtMon, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  CalendarIcon, XIcon, CaretLeftIcon, CaretRightIcon, WarningCircleIcon,
  TruckIcon, CaretDownIcon, GearIcon, CheckSquareIcon,
} from "@phosphor-icons/react";
import PagoCompraModal, { METODOS_PAGO as METODOS, cuentasPara, monedaDe, saldoMoneda, ESTADO_PAGO } from "@/pages/_purchases/PagoCompra";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Payables() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("unica");
  const [grouped, setGrouped] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [params, setParams] = useState({ dias_alerta_vencimiento: 7 });
  const [editParams, setEditParams] = useState(false);
  // calendar
  const today = new Date();
  const [cYear, setCYear] = useState(today.getFullYear());
  const [cMonth, setCMonth] = useState(today.getMonth() + 1);
  const [cal, setCal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, p, a, pr] = await Promise.all([
        api.get("/payables/by-supplier"),
        api.get("/payables"),
        api.get("/treasury/accounts"),
        api.get("/payables/params").catch(() => ({ data: { dias_alerta_vencimiento: 7 } })),
      ]);
      setGrouped(g.data); setItems(p.data); setAccounts(a.data); setParams(pr.data);
      return p.data;
    } finally { setLoading(false); }
  };
  useEffect(() => {
    (async () => {
      const list = await load();
      const fid = searchParams.get("factura");
      if (fid) {
        const f = (list || []).find((x) => x.id === fid);
        if (f) openPay(f);
        else toast.info("La factura seleccionada no tiene saldo pendiente");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCal = async () => {
    try { const { data } = await api.get("/payables/calendar", { params: { year: cYear, month: cMonth } }); setCal(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    if (tab === "calendar") loadCal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, cYear, cMonth]);

  const openPay = (f) => setPaying(f);

  const saveParams = async () => {
    try {
      const { data } = await api.put("/payables/params", { dias_alerta_vencimiento: Number(params.dias_alerta_vencimiento) || 0 });
      setParams(data); setEditParams(false); toast.success("Parámetro guardado"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <Layout
      title="Pagar Facturas"
      subtitle={`PRD 4.2 · ${grouped?.total_facturas || 0} facturas pendientes · ${grouped?.total_proveedores || 0} proveedores`}
      actions={
        <div className="flex items-center gap-2 text-xs">
          {editParams ? (
            <>
              <span className="text-text-muted">Alertar</span>
              <input type="number" min="0" value={params.dias_alerta_vencimiento} data-testid="cxp-dias-alerta"
                onChange={(e) => setParams({ ...params, dias_alerta_vencimiento: e.target.value })}
                className="w-16 bg-app border border-border rounded px-2 py-1 text-xs" />
              <span className="text-text-muted">días antes</span>
              <button onClick={saveParams} className="bg-agro-green text-black rounded px-2 py-1 font-medium" data-testid="cxp-dias-save">Guardar</button>
              <button aria-label="Cerrar" onClick={() => setEditParams(false)} className="text-text-muted"><XIcon size={14} /></button>
            </>
          ) : (
            <button onClick={() => setEditParams(true)} className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-md text-text-secondary hover:text-text-primary" data-testid="cxp-params-btn">
              <GearIcon size={14} /> Alerta: {params.dias_alerta_vencimiento} días
            </button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Kpi label="Deuda total" v={fmtL(grouped?.total_general || 0)} accent="green" />
        <Kpi label="Saldo vencido" v={fmtL(grouped?.total_vencido || 0)} accent="red" />
        <Kpi label={`Por vencer (${grouped?.dias_alerta ?? params.dias_alerta_vencimiento} días)`} v={fmtL(grouped?.total_por_vencer || 0)} accent="yellow" />
        <Kpi label="Proveedores con saldo" v={grouped?.total_proveedores || 0} accent="yellow" />
      </div>

      <div className="border-b border-border mb-4 flex gap-1">
        {[
          { k: "unica", l: "Vista única · Pago en lote", i: CheckSquareIcon },
          { k: "by-supplier", l: "Por proveedor y sucursal", i: TruckIcon },
          { k: "calendar", l: "Calendario", i: CalendarIcon },
        ].map((t) => (
          <button
            key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative flex items-center gap-2 ${tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"}`}
          >
            <t.i size={14} weight="duotone" /> {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "unica" && (
        <BatchPayView items={items} loading={loading} accounts={accounts} onDone={load} onPayOne={openPay} />
      )}

      {tab === "by-supplier" && (
        <div className="space-y-3">
          {loading ? (
            <div className="bg-card border border-border/60 rounded-lg p-10 text-center text-text-muted">Cargando…</div>
          ) : !grouped?.proveedores.length ? (
            <div className="bg-card border border-border/60 rounded-lg p-10 text-center text-text-muted">Sin deudas pendientes</div>
          ) : (
            grouped.proveedores.map((g) => {
              const k = g.clave || g.proveedor_id;
              const open = expanded[k] !== false; // default expanded
              return (
                <div key={k} className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid={`supplier-card-${g.proveedor_id}`}>
                  <button type="button" onClick={() => toggleExpand(k)} className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                      <CaretDownIcon size={14} className={`transition-transform ${open ? "rotate-0" : "-rotate-90"} text-text-muted`} />
                      <div className="text-left">
                        <div className="font-heading text-base font-semibold text-text-primary">{g.proveedor_nombre}
                          {g.subcuenta_nombre && <span className="text-sm text-agro-green font-normal" data-testid={`supplier-sub-${g.subcuenta_codigo}`}> · {g.subcuenta_codigo} ({g.subcuenta_nombre})</span>}</div>
                        <div className="text-xs text-text-muted">{g.proveedor_rtn} · {g.facturas.length} factura{g.facturas.length !== 1 ? "s" : ""}
                          {" · "}<b className="text-text-secondary" data-testid={`supplier-branch-${g.proveedor_id}`}>{g.branch_codigo ? `Sucursal ${g.branch_codigo}` : "Sin sucursal"}</b>
                          {g.auxiliar_codigo && <span className="font-mono"> · {g.auxiliar_codigo}</span>}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-5">
                      {g.total_vencido > 0 && (
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-wider text-red-400">Vencido</div>
                          <div className="text-sm font-medium text-red-400">{fmtL(g.total_vencido)}</div>
                        </div>
                      )}
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted">Adeudado</div>
                        <div className="font-heading text-lg font-semibold text-agro-yellow">{fmtL(g.total_adeudado)}</div>
                        {g.saldo_usd > 0 && <div className="text-[10px] text-text-muted">incluye {fmtMon(g.saldo_usd, "USD")} en facturas en dólares</div>}
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-border/60">
                      <table className="w-full">
                        <thead className="bg-sidebar/40">
                          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                            <th className="p-2.5 pl-12">Factura</th><th className="p-2.5">Fecha</th><th className="p-2.5">Vence</th>
                            <th className="p-2.5 text-right">Total</th><th className="p-2.5 text-right">Saldo</th>
                            <th className="p-2.5">Estado</th><th className="p-2.5 pr-5 text-right">Valor a pagar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.facturas.map((f) => (
                            <tr key={f.id} className={`border-t border-border/30 hover:bg-white/[0.02] ${f.vencida ? "bg-red-500/[0.03]" : f.por_vencer ? "bg-agro-yellow/[0.03]" : ""}`} data-testid={`payable-row-${f.numero_interno}`}>
                              <td className="p-2.5 pl-12">
                                <div className="font-mono text-xs text-agro-green">{f.numero_interno}</div>
                                <div className="text-[10px] text-text-muted">{f.numero_proveedor || "—"}</div>
                              </td>
                              <td className="p-2.5 text-xs text-text-secondary">{fmtDate(f.fecha)}</td>
                              <td className={`p-2.5 text-xs ${f.vencida ? "text-red-400 font-medium" : f.por_vencer ? "text-agro-yellow font-medium" : "text-text-secondary"}`}>
                                {f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : "—"}
                                {f.dias_vencimiento !== null && f.dias_vencimiento !== undefined && (
                                  <div className="text-[10px] opacity-80">
                                    {f.vencida ? `Vencida hace ${Math.abs(f.dias_vencimiento)}d` : f.por_vencer ? `Vence en ${f.dias_vencimiento}d` : `En ${f.dias_vencimiento}d`}
                                  </div>
                                )}
                              </td>
                              <td className="p-2.5 text-right text-sm">{fmtMon(f.total_moneda ?? f.total, monedaDe(f))}
                                {monedaDe(f) === "USD" && <div className="text-[10px] text-text-muted">{fmtL(f.total)} · tasa {f.tipo_cambio}</div>}</td>
                              <td className="p-2.5 text-right text-sm font-medium text-agro-yellow">{fmtMon(saldoMoneda(f), monedaDe(f))}
                                {monedaDe(f) === "USD" && <div className="text-[10px] text-text-muted font-normal">{fmtL(f.saldo_pendiente)}</div>}</td>
                              <td className="p-2.5"><DueBadge f={f} /></td>
                              <td className="p-2.5 pr-5 text-right">
                                <button onClick={() => openPay(f)} data-testid={`pay-${f.numero_interno}`} className="text-xs bg-agro-green text-black font-medium rounded px-3 py-1.5 hover:bg-agro-green-hover">Pagar</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "calendar" && cal && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button aria-label="Anterior" onClick={() => { if (cMonth === 1) { setCMonth(12); setCYear(cYear - 1); } else setCMonth(cMonth - 1); }} data-testid="cal-prev" className="p-2 border border-border rounded-md text-text-muted hover:text-text-primary"><CaretLeftIcon size={14} /></button>
              <div className="text-center min-w-[180px]">
                <div className="text-[10px] uppercase tracking-wider text-text-muted">Vencimientos</div>
                <h3 className="font-heading text-lg font-semibold first-letter:uppercase">{new Date(cYear, cMonth - 1).toLocaleDateString("es-HN", { month: "long", year: "numeric" })}</h3>
              </div>
              <button aria-label="Siguiente" onClick={() => { if (cMonth === 12) { setCMonth(1); setCYear(cYear + 1); } else setCMonth(cMonth + 1); }} data-testid="cal-next" className="p-2 border border-border rounded-md text-text-muted hover:text-text-primary"><CaretRightIcon size={14} /></button>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Total del mes</div>
              <div className="font-heading text-xl text-agro-green font-semibold" data-testid="cal-total">{fmtL(cal.total_mes)}</div>
              <div className="text-xs text-text-muted">{cal.count} facturas</div>
            </div>
          </div>
          <CalendarGrid year={cYear} month={cMonth} byDay={cal.by_day} onPick={openPay} />
        </div>
      )}

      {paying && (
        <PagoCompraModal factura={paying} accounts={accounts} onClose={() => setPaying(null)}
          onSaved={() => { load(); if (tab === "calendar") loadCal(); }} />
      )}
    </Layout>
  );
}

function BatchPayView({ items, loading, accounts, onDone, onPayOne }) {
  const [rows, setRows] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const def = { sel: false, monto: "", metodo: "transferencia", cuenta_id: "", referencia: "", fecha: todayStr() };
  const get = (id) => rows[id] || def;
  const upd = (id, patch) => setRows((r) => ({ ...r, [id]: { ...(r[id] || def), ...patch } }));
  const busqueda = useLiveSearch(items, (f) => [f.proveedor_nombre, f.proveedor_codigo, f.numero_interno, f.numero_proveedor, f.subcuenta_nombre],
    { estado: [q, setQ] });
  const filtered = busqueda.resultados;
  const selected = filtered.filter((f) => get(f.id).sel && monedaDe(f) === "HNL");
  const totalSel = selected.reduce((s, f) => s + (Number(get(f.id).monto) || 0), 0);

  const toggle = (f, v) => upd(f.id, { sel: v, monto: v && !get(f.id).monto ? f.saldo_pendiente : get(f.id).monto });
  const enLote = (f) => monedaDe(f) === "HNL";
  const pagar = async () => {
    if (!selected.length) return toast.error("Selecciona al menos una factura");
    for (const f of selected) {
      const r = get(f.id);
      if (!String(r.referencia).trim()) return toast.error(`Falta referencia de pago en ${f.numero_interno}`);
      if (r.metodo !== "tarjeta_credito" && !r.cuenta_id) return toast.error(`Falta cuenta de tesorería en ${f.numero_interno}`);
      if (!(Number(r.monto) > 0)) return toast.error(`Valor a pagar inválido en ${f.numero_interno}`);
    }
    if (!window.confirm(`¿Registrar ${selected.length} pago(s) por ${fmtL(totalSel)}?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-payments/batch", {
        pagos: selected.map((f) => {
          const r = get(f.id);
          return { factura_id: f.id, monto: Number(r.monto), metodo: r.metodo, cuenta_id: r.cuenta_id || null, referencia: r.referencia, fecha: r.fecha || null };
        }),
      });
      toast.success(`${data.count} pagos registrados · ${fmtL(data.total)}`);
      setRows({});
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const inp = "bg-app border border-border rounded px-2 py-1 text-xs";
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="cxp-batch-view">
      <div className="p-3 border-b border-border/60 flex flex-wrap gap-3 items-center justify-between">
        <SearchBox busqueda={busqueda} placeholder="Buscar proveedor o factura…" testid="cxp-buscar" label="Buscar cuentas por pagar" className="max-w-md" />
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">{selected.length} seleccionada(s) · <b className="text-agro-green">{fmtL(totalSel)}</b></span>
          <button onClick={pagar} disabled={saving || !selected.length} data-testid="cxp-batch-pay"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50">
            {saving ? "Registrando…" : "Pagar seleccionadas"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-2 pl-4"></th><th className="p-2">Fecha</th><th className="p-2">Proveedor</th><th className="p-2">N° ref</th>
              <th className="p-2">Vencimiento</th><th className="p-2 text-right">Valor adeudado</th><th className="p-2 text-right">Valor a pagar</th>
              <th className="p-2">Método</th><th className="p-2">Cuenta</th><th className="p-2">Ref. pago</th><th className="p-2">Fecha pago</th><th className="p-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="12" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan="12" className="p-12 text-center text-text-muted">{busqueda.activa ? `Sin resultados para «${busqueda.texto}».` : "Sin deudas pendientes"}</td></tr>
              : filtered.map((f) => {
                const r = get(f.id);
                return (
                  <tr key={f.id} className={`border-t border-border/40 ${f.vencida ? "bg-red-500/[0.03]" : f.por_vencer ? "bg-agro-yellow/[0.03]" : ""}`} data-testid={`batch-row-${f.numero_interno}`}>
                    <td className="p-2 pl-4"><input type="checkbox" checked={r.sel && enLote(f)} disabled={!enLote(f)} title={enLote(f) ? "" : "Factura en dólares: se paga con «Detalle» indicando la tasa"}
                      onChange={(e) => toggle(f, e.target.checked)} className="w-4 h-4 accent-agro-green disabled:opacity-30" data-testid={`batch-sel-${f.numero_interno}`} /></td>
                    <td className="p-2 text-xs text-text-secondary">{fmtDate(f.fecha)}</td>
                    <td className="p-2 text-sm">{f.proveedor_nombre}{f.subcuenta_codigo && <div className="text-[10px] text-agro-green">{f.subcuenta_codigo}</div>}</td>
                    <td className="p-2"><div className="font-mono text-xs text-agro-green">{f.numero_interno}</div><div className="text-[10px] text-text-muted">{f.numero_proveedor}</div></td>
                    <td className={`p-2 text-xs ${f.vencida ? "text-red-400 font-medium" : f.por_vencer ? "text-agro-yellow font-medium" : "text-text-secondary"}`}>
                      {f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : "—"} <DueBadge f={f} small />
                    </td>
                    <td className="p-2 text-right text-sm font-medium text-agro-yellow">{fmtMon(saldoMoneda(f), monedaDe(f))}
                      {!enLote(f) && <div className="text-[10px] text-text-muted font-normal">{fmtL(f.saldo_pendiente)}</div>}</td>
                    <td className="p-2 text-right">{enLote(f)
                      ? <input type="number" step="0.01" value={r.monto} placeholder="0.00" onChange={(e) => upd(f.id, { monto: e.target.value, sel: true })} className={`${inp} w-24 text-right`} data-testid={`batch-monto-${f.numero_interno}`} />
                      : <button onClick={() => onPayOne(f)} className="text-xs bg-agro-green text-black font-medium rounded px-2 py-1" data-testid={`batch-pagar-usd-${f.numero_interno}`}>Pagar (US$)</button>}</td>
                    <td className="p-2">
                      <select value={r.metodo} onChange={(e) => upd(f.id, { metodo: e.target.value, cuenta_id: "" })} className={inp}>
                        {METODOS.filter((m) => m.v !== "nota_credito").map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      <select value={r.cuenta_id} onChange={(e) => upd(f.id, { cuenta_id: e.target.value })} className={`${inp} w-40`}>
                        <option value="">{r.metodo === "tarjeta_credito" ? "TC por pagar" : "— Cuenta —"}</option>
                        {cuentasPara(accounts, r.metodo).map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
                      </select>
                    </td>
                    <td className="p-2"><input value={r.referencia} onChange={(e) => upd(f.id, { referencia: e.target.value })} placeholder="Obligatoria" className={`${inp} w-28`} data-testid={`batch-ref-${f.numero_interno}`} /></td>
                    <td className="p-2"><input type="date" value={r.fecha} onChange={(e) => upd(f.id, { fecha: e.target.value })} className={inp} /></td>
                    <td className="p-2 pr-4 text-right"><button onClick={() => onPayOne(f)} className="text-[10px] text-text-muted hover:text-agro-green underline" title="Pago con retenciones / nota de crédito">Detalle</button></td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DueBadge({ f, small }) {
  const cls = small ? "ml-1 text-[9px] px-1 py-0" : "text-[10px] px-2 py-0.5";
  if (f.vencida) return <span className={`${cls} uppercase rounded bg-red-500/10 text-red-400 border border-red-500/30 inline-flex items-center gap-1`}><WarningCircleIcon size={10} weight="fill" /> Vencida</span>;
  if (f.por_vencer) return <span className={`${cls} uppercase rounded bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30`}>Por vencer</span>;
  if (small) return null;
  return <span className={`${cls} uppercase rounded bg-app text-text-secondary border border-border`}>{ESTADO_PAGO[f.estado] || f.estado}</span>;
}

function CalendarGrid({ year, month, byDay, onPick }) {
  const first = new Date(year, month - 1, 1);
  const startDow = first.getDay();
  const lastDay = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const today = new Date();
  const isToday = (d) => d && year === today.getFullYear() && month - 1 === today.getMonth() && d === today.getDate();
  const tStr = today.toISOString().slice(0, 10);
  const dows = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

  return (
    <div className="p-4">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dows.map((d) => <div key={d} className="text-[10px] uppercase tracking-wider text-text-muted text-center py-1 capitalize">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const key = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const dayInv = byDay[key] || [];
          const sumDia = dayInv.reduce((s, x) => s + x.saldo_pendiente, 0);
          const isPast = key < tStr;
          return (
            <div key={i} className={`relative aspect-square border rounded-md p-1.5 overflow-hidden transition-all ${dayInv.length ? (isPast ? "bg-red-500/10 border-red-500/30" : "bg-agro-yellow/10 border-agro-yellow/30") : "bg-app border-border/40"} ${isToday(d) ? "ring-2 ring-agro-green" : ""}`}>
              <div className="flex justify-between items-start">
                <span className={`text-xs font-medium ${isToday(d) ? "text-agro-green" : "text-text-secondary"}`}>{d}</span>
                {dayInv.length > 0 && <span className={`text-[9px] font-bold ${isPast ? "text-red-400" : "text-agro-yellow"}`}>{dayInv.length}</span>}
              </div>
              {dayInv.length > 0 && (
                <div className="absolute inset-x-1.5 bottom-1.5 space-y-0.5">
                  <div className={`text-[10px] font-medium truncate ${isPast ? "text-red-400" : "text-agro-yellow"}`}>{fmtL(sumDia)}</div>
                  {dayInv.slice(0, 1).map((inv) => (
                    <button key={inv.id} onClick={() => onPick(inv)} className="block w-full text-left text-[9px] text-text-secondary hover:text-agro-green truncate">{inv.proveedor_nombre}</button>
                  ))}
                  {dayInv.length > 1 && <div className="text-[9px] text-text-muted">+{dayInv.length - 1} más</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, v, accent }) {
  const map = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400" };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-2xl font-semibold mt-1 ${map[accent]}`}>{v}</div>
    </div>
  );
}
